import { Injectable } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class ExportService {
  //  Use base href to resolve asset paths correctly in all environments
  private getAssetUrl(path: string): string {
    // Gets the <base href> value set by Angular (e.g., /warranties/icd/)
    const baseEl = document.querySelector('base');
    const baseHref = baseEl?.getAttribute('href') ?? '/';
    const base = window.location.origin + baseHref.replace(/\/$/, '');
    return `${base}/${path.replace(/^\//, '')}`;
  }

  // Robust download helper — adapts strategy based on environment:
  //   - Localhost: Use blob URLs (fast, native browser support)
  //   - INT/PROD: Use data URLs to bypass CSP restrictions on blob: protocol
  private triggerDownload(
    content: Blob | string,
    fileName: string,
    mimeType: string,
    onComplete?: (success: boolean, message: string) => void
  ): void {
    try {
      // Strategy 1 – IE/Edge legacy
      const nav = window.navigator as any;
      if (nav.msSaveBlob) {
        const blob =
          content instanceof Blob
            ? content
            : new Blob([content as string], { type: mimeType });
        nav.msSaveBlob(blob, fileName);
        onComplete?.(true, `${fileName} downloaded successfully.`);
        return;
      }

      // Detect hosted environments (INT/PROD) that may have CSP restrictions
      const isHostedEnv =
        window.location.hostname.includes('.aws.cloud.bmw') ||
        window.location.hostname.includes('.aws.bmw.cloud');

      // Strategy 2 – For hosted environments, convert blob to data URL to bypass CSP
      if (content instanceof Blob && isHostedEnv) {
        // console.log('[triggerDownload] Using data URL for hosted environment:', window.location.hostname);

        const reader = new FileReader();
        reader.onload = () => {
          try {
            const base64 = (reader.result as string).split(',')[1];
            // Use application/octet-stream to force download instead of opening in browser
            const dataUrl = `data:application/octet-stream;base64,${base64}`;

            const a = document.createElement('a');
            a.href = dataUrl;
            a.download = fileName;
            a.style.display = 'none';
            document.body.appendChild(a);
            a.click();

            setTimeout(() => {
              document.body.removeChild(a);
              onComplete?.(true, `${fileName} downloaded successfully.`);
            }, 500);
          } catch (err) {
            console.error('[triggerDownload] Data URL conversion failed:', err);
            onComplete?.(false, `Download failed for ${fileName}.`);
          }
        };

        reader.onerror = () => {
          console.error('[triggerDownload] FileReader error');
          onComplete?.(false, `Download failed for ${fileName}.`);
        };

        reader.readAsDataURL(content);
        return;
      }

      // Strategy 3 – data URI (for string content)
      if (!(content instanceof Blob)) {
        const base64 = btoa(unescape(encodeURIComponent(content as string)));
        const dataUri = `data:${mimeType};base64,${base64}`;
        const a = document.createElement('a');
        a.href = dataUri;
        a.download = fileName;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        onComplete?.(true, `${fileName} downloaded successfully.`);
        return;
      }

      // Strategy 4 – Blob URL (for localhost and non-CSP-restricted environments)
      // console.log('[triggerDownload] Using blob URL for localhost');
      const url = URL.createObjectURL(content as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        onComplete?.(true, `${fileName} downloaded successfully.`);
      }, 500);
    } catch (e) {
      console.error('[triggerDownload] Failed:', e);
      onComplete?.(false, `Download failed for ${fileName}.`);
    }
  }

  // Build a payload you can POST to an export API if desired
  routerLink = window.location.href;
  buildPayload(
    criteria: any,
    options?: {
      filters?: any;
      sort?: any;
      visibleColumns?: string[];
      selectedIds?: any[];
    }
  ) {
    return {
      criteria: criteria ?? {},
      filters: options?.filters ?? {},
      sort: options?.sort ?? {},
      visibleColumns: options?.visibleColumns ?? [],
      selectedIds: options?.selectedIds ?? [],
      requestedAt: new Date().toISOString(),
    };
  }

  // Map raw API rows to the required fields for ICD table
  mapICDRows(rawRows: any[]): any[] {
    return (rawRows || []).map((row: any) => ({
      StopSale: row['StopSale'] ?? null,
      DefectCode: row['DefectCode'] ?? null,
      DefectDescription: row['DefectDescription'] ?? null,
      BulletinNumber: row['BulletinNumber'] ?? null,
      RecallNumber: row['RecallNumber'] ?? null,
      CampaignStartDate: row['CampaignStartDate'] ?? null,
      DealerInventor: row['DealerInventor'] ?? null,
      DemoLoaner: row['DemoLoaner'] ?? null,
      PreOwned: row['PreOwned'] ?? null,
      TotalVehicleCount: row['TotalVehicleCount'] ?? null,
      Color: row['Color'] ?? null,
    }));
  }

  // Export with ordered "details" rows (label,value) at the top
  exportCsvWithDetails(
    fileName: string,
    rows: any[],
    centerCodeDetails: string[] = [],
    columns?: string[],
    columnHeaders?: string[],
    details?: Array<{ label: string; value: any }>,
    options?: { title?: string; addBom?: boolean },
    onComplete?: (success: boolean, message: string) => void
  ) {
    const cols =
      columns && columns.length
        ? columns
        : rows.length
          ? Object.keys(rows[0])
          : [];
    const headerLabels =
      columnHeaders && columnHeaders.length ? columnHeaders : cols;

    const escape = (val: any) => {
      const s = String(val ?? '');
      const escaped = s.replace(/"/g, '""');
      return /[",\n]/.test(escaped) ? `"${escaped}"` : escaped;
    };

    const lines: string[] = [];
    centerCodeDetails.forEach((text) => {
      lines.push(`${escape(text)}`);
    });
    lines.push('');
    if (details && details.length) {
      const title = options?.title;
      if (title) {
        lines.push(`${escape(title)},`);
      }
      details.forEach((d) => {
        const valueStr = Array.isArray(d.value)
          ? d.value.join('; ')
          : String(d.value ?? '');
        lines.push(`${escape(d.label)},${escape(valueStr)}`);
      });
      lines.push('');
    }

    const header = headerLabels.join(',');
    const body = rows
      .map((r) => cols.map((c) => escape(r[c])).join(','))
      .join('\n');
    lines.push(header);
    lines.push(body);

    const prefix = options?.addBom ? '\uFEFF' : '';
    const csv = prefix + lines.join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    this.triggerDownload(
      blob,
      `${fileName || 'export'}.csv`,
      'text/csv;charset=utf-8;',
      onComplete
    );
  }

  // Format timestamp in US/Eastern (EST/EDT) as MM/DD/YYYY  hh:mm:ss AM EST
  formatEstTimestamp(date?: Date): string {
    const d = date ?? new Date();
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    }).formatToParts(d);

    const get = (type: string) =>
      parts.find((p) => p.type === type)?.value ?? '';
    const mm = get('month');
    const dd = get('day');
    const yyyy = get('year');
    const hh = get('hour');
    const min = get('minute');
    const ss = get('second');
    const dayPeriod = (get('dayPeriod') || '').toUpperCase();
    // Append EST per request (note: New York uses EDT during daylight saving)
    return `${mm}/${dd}/${yyyy}  ${hh}:${min}:${ss} ${dayPeriod} EST`;
  }

  // Build a printable HTML document with details + table
  private buildPrintHtmlWithDetails(
    title: string | undefined,
    subtitle: string | undefined,
    details: Array<{ label: string; value: any }> = [],
    columns: string[] = [],
    rows: any[] = []
  ): string {
    const escapeHtml = (s: any) =>
      String(s ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

    const detailRows = details
      .map((d) => {
        const val = Array.isArray(d.value) ? d.value.join('; ') : d.value;
        return `<tr><th>${escapeHtml(d.label)}</th><td>${escapeHtml(val)}</td></tr>`;
      })
      .join('');

    const headerRow = columns.map((c) => `<th>${escapeHtml(c)}</th>`).join('');
    const bodyRows = rows
      .map(
        (r) =>
          `<tr>${columns.map((c) => `<td>${escapeHtml(r?.[c])}</td>`).join('')}</tr>`
      )
      .join('');

    return `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(title ?? 'Export')}</title>
  <style>
    body { font-family: Arial, Helvetica, sans-serif; padding: 24px; }
    h1 { font-size: 18px; margin: 0 0 16px; }
    h2 { font-size: 14px; margin: 0 0 8px; color: #333; }
    table { border-collapse: collapse; width: 100%; }
    .details th { text-align: left; width: 280px; padding: 6px 8px; background: #f7f7f7; }
    .details td { padding: 6px 8px; }
    .details { margin-bottom: 16px; }
    .grid th, .grid td { border: 1px solid #ddd; padding: 6px 8px; }
    .grid th { background: #fafafa; text-align: left; }
    /* Zebra striping to mirror AG Grid oddRowBackgroundColor */
    .grid tbody tr:nth-child(odd) { background-color: #f5f5f5; }
    @media print {
      .no-print { display: none; }
    }
  </style>
</head>
<body>
  ${title ? `<h1>${escapeHtml(title)}</h1>` : ''}
  ${subtitle ? `<h2>${escapeHtml(subtitle)}</h2>` : ''}
  ${details.length ? `<strong>Selected Criteria:</strong>` : ''}
  ${details.length ? `<table class="details">${detailRows}</table>` : ''}
  <table class="grid">
    <thead><tr>${headerRow}</tr></thead>
    <tbody>${bodyRows}</tbody>
  </table>
  <div class="no-print" style="margin-top:12px;">
    <button onclick="window.print()">Print</button>
  </div>
</body>
</html>`;
  }

  // Open a print dialog using hidden iframe to avoid opening new tab
  printWithDetails(
    rows: any[],
    columns: string[],
    details?: Array<{ label: string; value: any }>,
    options?: { title?: string; subtitle?: string }
  ) {
    const html = this.buildPrintHtmlWithDetails(
      options?.title,
      options?.subtitle,
      details ?? [],
      columns ?? [],
      rows ?? []
    );

    // Use iframe approach for all environments to avoid new tab
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = 'none';
    document.body.appendChild(iframe);

    const iframeDoc = iframe.contentWindow?.document;
    if (iframeDoc) {
      iframeDoc.open();
      iframeDoc.write(html);
      iframeDoc.close();

      setTimeout(() => {
        try {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
          // Remove iframe after print
          setTimeout(() => {
            document.body.removeChild(iframe);
          }, 1000);
        } catch (e) {
          console.error('Print failed:', e);
          document.body.removeChild(iframe);
        }
      }, 150);
    }
  }

  // Direct PDF download using jsPDF + autoTable
  async exportPdfWithDetails(
    fileName: string,
    rows: any[],
    columns: string[],
    columnHeaders?: string[],
    details?: Array<{ label: string; value: any }>,
    options?: {
      title?: string; // Center heading, e.g., "Inventory Campaign Details"
      subtitle?: string; // Subtitle above Selected Criteria
      dealerTitle?: string; // Title line e.g., "Dealer: XX-YY"
      disclaimer?: string; // Disclaimer text shown in red on first page
      printedOn?: string; // Footer "Printed on" timestamp text
      orientation?: 'portrait' | 'landscape';
      // Styling options to mirror AG Grid
      rowHeight?: number; // px-like height per row
      cellPadding?: number; // padding inside cells
      headerFillColor?: string | number[]; // header background color
      headerTextColor?: string | number[]; // header text color
      textColor?: string | number[]; // body text color
      zebraFillColor?: string | number[]; // alternate row background color
      columnWidths?: number[]; // array of widths in points
      renderIcons?: boolean; // draw icons for specific columns
      pinnedBottomFillColor?: string | number[]; // background color for totals row
    },
    onComplete?: (success: boolean, message: string) => void
  ) {
    try {
      const jsPDFModule = await import('jspdf');
      await import('jspdf-autotable');
      const jsPDF =
        (jsPDFModule as any).jsPDF ||
        (jsPDFModule as any).default ||
        jsPDFModule;
      const orientation = options?.orientation ?? 'landscape';
      const doc = new jsPDF({
        orientation,
        unit: 'pt',
        format: 'letter',
      }) as any;
      const totalPagesExp = '{total_pages_count_string}';

      // Load image and convert to base64 data URL for reliable cross-environment embedding
      const loadImageAsDataUrl = async (src: string): Promise<string> => {
        try {
          // First, fetch the image as blob to avoid CORS issues
          const response = await fetch(src);
          if (!response.ok) throw new Error(`HTTP ${response.status}`);

          const blob = await response.blob();

          // Convert blob to base64 data URL
          return new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = () =>
              reject(new Error('Failed to convert image to data URL'));
            reader.readAsDataURL(blob);
          });
        } catch (err) {
          throw new Error(`Failed to load image: ${src} - ${err}`);
        }
      };

      // Preload images as base64 data URLs from Angular assets
      // Use absolute paths (starting with /) - same as other components
      // ← ONLY load icons if renderIcons is true AND column exists in export
      let stopSaleImg: string | undefined;
      let oraIconY: string | undefined;
      let oraIconN: string | undefined;
      let oraIconS: string | undefined;
      let remedyStatusIconY: string | undefined;
      let remedyStatusIconN: string | undefined;

      if (options?.renderIcons) {
        // ← only load icons that are actually needed by checking columns array
        const hasStopSale = columns.includes('StopSale');
        const hasORA = columns.includes('OverallRemedyAvailable');
        const hasRemedy = columns.includes('RemedyStatusAllOpenCampaigns');

        // ← load in parallel using Promise.all — faster than sequential awaits
        const iconLoads = await Promise.allSettled([
          hasStopSale
            ? loadImageAsDataUrl('/assets/images/stop_bkp.png')
            : Promise.resolve(undefined),
          hasORA
            ? loadImageAsDataUrl('/assets/images/ora_y.png')
            : Promise.resolve(undefined),
          hasORA
            ? loadImageAsDataUrl('/assets/images/ora_n.png')
            : Promise.resolve(undefined),
          hasORA
            ? loadImageAsDataUrl('/assets/images/ora_s.png')
            : Promise.resolve(undefined),
          hasRemedy
            ? loadImageAsDataUrl('/assets/images/remedy_Y.png')
            : Promise.resolve(undefined),
          hasRemedy
            ? loadImageAsDataUrl('/assets/images/remedy_N.png')
            : Promise.resolve(undefined),
        ]);

        stopSaleImg =
          iconLoads[0].status === 'fulfilled' ? iconLoads[0].value : undefined;
        oraIconY =
          iconLoads[1].status === 'fulfilled' ? iconLoads[1].value : undefined;
        oraIconN =
          iconLoads[2].status === 'fulfilled' ? iconLoads[2].value : undefined;
        oraIconS =
          iconLoads[3].status === 'fulfilled' ? iconLoads[3].value : undefined;
        remedyStatusIconY =
          iconLoads[4].status === 'fulfilled' ? iconLoads[4].value : undefined;
        remedyStatusIconN =
          iconLoads[5].status === 'fulfilled' ? iconLoads[5].value : undefined;

        console.log(
          '[PDF] Icons loaded — StopSale:',
          !!stopSaleImg,
          'ORA:',
          !!oraIconY,
          'Remedy:',
          !!remedyStatusIconY
        );
      } else {
        // console.log('[PDF] Icons skipped — renderIcons is false');
      }

      const pageWidth = (doc.internal as any).pageSize.getWidth();
      const pageHeight = (doc.internal as any).pageSize.getHeight();
      const marginLeft = 40;
      const marginRight = 40;

      // Header + Footer on every page
      const headerTitle = options?.title ?? 'Inventory Campaign Details';
      const headerSubtitle = options?.subtitle;
      const dealerTitle = options?.dealerTitle ?? '';
      const printedOn = options?.printedOn ?? '';
      (doc as any).autoTableSetDefaults({
        didDrawPage: (data: any) => {
          // Header: center heading, left-aligned dealer title on FIRST page only
          const pageNum = doc.getCurrentPageInfo().pageNumber;
          if (pageNum === 1) {
            doc.setFontSize(14);
            doc.setTextColor(0);
            doc.text(headerTitle, pageWidth / 2, 30, { align: 'center' });
            if (dealerTitle) {
              doc.setFontSize(11);
              doc.text(dealerTitle, marginLeft, 48);
              // Horizontal rule separating dealer title and Selected Criteria
              if (pageNum === 1) {
                doc.setDrawColor(180);
                doc.setLineWidth(1);
                doc.line(marginLeft, 54, pageWidth - marginRight, 54);
              }
            }
          }

          // Footer: printed on left, page X of Y right
          const str = `Page ${pageNum} of ${totalPagesExp}`;
          const footerY = pageHeight - 24;
          doc.setDrawColor(180);
          doc.setLineWidth(1);
          doc.line(
            marginLeft,
            footerY - 15,
            pageWidth - marginRight,
            footerY - 15
          );
          if (printedOn) {
            doc.setFontSize(8);
            doc.text(`Printed on: ${printedOn}`, marginLeft, footerY);
          }
          doc.setFontSize(8);
          // Ensure right alignment across jsPDF versions
          try {
            (doc as any).text(
              str,
              pageWidth - marginRight,
              footerY,
              null,
              null,
              'right'
            );
          } catch {
            const tw = (doc as any).getTextWidth(str);
            (doc as any).text(str, pageWidth - marginRight - tw, footerY);
          }
        },
      });

      // Details block as two-column table (prefixed by "Selected Criteria:")
      const startYDetails = 80; // leave room for header and rule
      // Optional subtitle above Selected Criteria
      if (headerSubtitle) {
        doc.setFontSize(10);
        try {
          (doc as any).setFont(undefined, 'bold');
        } catch {}
        doc.text(headerSubtitle, marginLeft, startYDetails - 24);
        try {
          (doc as any).setFont(undefined, 'normal');
        } catch {}
        //thin horizontal rule below subtitle and above details
        doc.setDrawColor(180);
        doc.setLineWidth(1);
        doc.line(
          marginLeft,
          startYDetails - 20,
          pageWidth - marginRight,
          startYDetails - 20
        );
      }
      const detailBody = (details ?? []).map((d) => [
        String(d.label),
        Array.isArray(d.value) ? d.value.join('; ') : String(d.value ?? ''),
      ]);
      if (detailBody.length) {
        // Subtitle label
        doc.setFontSize(10);
        doc.text('Selected Criteria:', marginLeft, startYDetails - 10);

        doc.autoTable({
          head: [['Label', 'Value']],
          body: detailBody,
          startY: startYDetails,
          styles: {
            fontSize: 7,
            cellPadding: options?.cellPadding ?? 8,
            minCellHeight: Math.max(
              20,
              (options?.rowHeight ?? 28) - 2 * (options?.cellPadding ?? 8)
            ),
            valign: 'middle',
            overflow: 'linebreak',
          },
          headStyles: {
            fillColor: options?.headerFillColor ?? [240, 240, 240],
            textColor: options?.headerTextColor ?? [14, 14, 14],
            overflow: 'linebreak',
          },
          margin: { left: marginLeft, right: marginRight },
          theme: 'grid',
        });
      }

      // Disclaimer in red below details, before table (first page)
      const afterDetailsY = (doc as any).lastAutoTable
        ? (doc as any).lastAutoTable.finalY + 20
        : startYDetails + 20;
      if (options?.disclaimer) {
        doc.setTextColor(220, 20, 60);
        doc.setFontSize(8);
        doc.text(`${options.disclaimer}`, marginLeft, afterDetailsY, {
          maxWidth: pageWidth - marginLeft - marginRight,
        });
        doc.setTextColor(0);
      }

      // Data table
      const startY = (doc as any).lastAutoTable
        ? (doc as any).lastAutoTable.finalY + 16
        : 60;
      const headLabels =
        columnHeaders && columnHeaders.length ? columnHeaders : columns;
      const tableHead = [headLabels];
      const tableBody = (rows ?? []).map((r) =>
        columns.map((c) => String(r?.[c] ?? ''))
      );
      // Normalize column widths to fit within page margins
      const columnStyles: Record<number, any> = {};
      const availableWidth = pageWidth - marginLeft - marginRight;
      if (options?.columnWidths && options.columnWidths.length) {
        const rawSum =
          options.columnWidths.reduce((s, w) => s + (w || 0), 0) || 1;
        const scale = availableWidth / rawSum;
        options.columnWidths.forEach((w, idx) => {
          const safeWidth = Math.max(20, (w || 20) * scale);
          columnStyles[idx] = { cellWidth: safeWidth };
        });
      }

      // console.log('[exportPdfWithDetails] Rendering main table - tableHead:', tableHead.length, 'rows, tableBody:', tableBody.length, 'rows');
      const remedyRenderByCell = new Map<
        string,
        Array<{ text: string; isRed: boolean; iconCode?: string }>
      >();
      const getPdfLineHeight = (fontSize: number): number => {
        // Use a stable point-based line-height for custom remedy rendering.
        // This avoids environment-specific scale-factor differences that can cause text overlap.
        return Math.max(fontSize * 1.25, 8);
      };
      const getRemedyCellWidth = (cellData: any): number => {
        const fromCell = Number(
          cellData?.cell?.width ?? cellData?.cell?._width ?? 0
        );
        const fromColumn = Number(cellData?.column?.width ?? 0);
        const resolved =
          fromCell > 0 ? fromCell : fromColumn > 0 ? fromColumn : 160;
        return resolved;
      };
      const bodyFontSize = 7;
      const requestedPadding = Number(options?.cellPadding ?? 5);
      const bodyCellPadding = Math.min(Math.max(requestedPadding, 2), 5);
      const compactMinCellHeight = Math.max(
        getPdfLineHeight(bodyFontSize) + 2 * bodyCellPadding,
        10
      );
      //page-break guard before main data table to ensure details + disclaimer are not separated from table by a page break
      let tableStartY = afterDetailsY + (options?.disclaimer ? 18 : 0);
      const estimatedHeaderHeight = 9 + 2 * (options?.cellPadding ?? 5) + 10;
      const bottomMargin = 48;
      if (tableStartY + estimatedHeaderHeight * 2 > pageHeight - bottomMargin) {
        doc.addPage();
        tableStartY = 60;
      }
      doc.autoTable({
        head: tableHead,
        body: tableBody,
        startY: tableStartY,

        // Keep rows together so custom Remedy Status drawing stays inside one cell box.
        pageBreak: 'auto',
        rowPageBreak: 'avoid',
        showHead: 'everyPage',

        styles: {
          fontSize: bodyFontSize,
          cellPadding: bodyCellPadding,
          minCellHeight: compactMinCellHeight,
          valign: 'middle',
          overflow: 'linebreak',
          textColor: options?.textColor ?? [26, 26, 26], // #1a1a1a
        },
        headStyles: {
          fillColor: [227, 242, 253],
          textColor: options?.headerTextColor ?? [14, 14, 14], // #0e0e0e
          overflow: 'linebreak',
        },
        alternateRowStyles: {
          fillColor: options?.zebraFillColor ?? [245, 245, 245],
        },
        margin: { left: marginLeft, right: marginRight },
        columnStyles,
        didParseCell: (data: any) => {
          if (data.section !== 'body') return;
          try {
            const colId = columns[data.column.index];
            const rowObj = (rows as any[])[data.row.index] ?? {};
            const rowIndex = data.row?.index ?? -1;
            const rawRow = (rows as any[])[rowIndex] ?? {};
            const isTotalsRow =
              rawRow?.isTotalsRow === true || rawRow?._isTotalsRow === true;
            if (isTotalsRow) {
              data.cell.styles.fillColor = options?.pinnedBottomFillColor ?? [
                227, 242, 253,
              ];
              data.cell.styles.fontStyle = 'bold';
              data.cell.styles.textColor = [14, 14, 14];
              return; // skip all other styling for this row
            }
            // If StopSale icon will be rendered, suppress underlying text (e.g., 'Y')
            if (colId === 'StopSale') {
              const raw = rowObj[colId];
              const s = String(raw).toLowerCase();
              const truthy =
                raw === true ||
                raw === 1 ||
                s === 'y' ||
                s === 'yes' ||
                s === 'true';
              const falsyNo =
                raw === false ||
                raw === 0 ||
                s === 'n' ||
                s === 'no' ||
                s === 'false';
              if (truthy || falsyNo) {
                // Hide any underlying text (e.g., 'Y' or 'N') in the StopSale cell
                data.cell.text = '';
              }
            }
            if (colId === 'OverallRemedyAvailable') {
              const raw = rowObj[colId];
              const s = String(raw).toLowerCase();
              const truthy =
                raw === true ||
                raw === 1 ||
                s === 'y' ||
                s === 'yes' ||
                s === 'true';
              const falsyNo =
                raw === false ||
                raw === 0 ||
                s === 'n' ||
                s === 'no' ||
                s === 'false';
              const exclamationMark = raw === 's' || raw === 'S';
              if (
                truthy ||
                falsyNo ||
                exclamationMark ||
                raw === ' ' ||
                raw === null ||
                raw === undefined
              ) {
                // Hide any underlying text (e.g., 'Y' or 'N') in the StopSale cell
                data.cell.text = '';
              }
            }
            // Check if StopSale is 'Y' for this row
            const stopSaleRaw = rowObj['StopSale'];
            const stopSaleStr = String(stopSaleRaw ?? '').toLowerCase();
            const isStopSaleY =
              stopSaleRaw === true ||
              stopSaleRaw === 1 ||
              stopSaleStr === 'y' ||
              stopSaleStr === 'yes' ||
              stopSaleStr === 'true';

            // REMEDY CELL — apply per-item color using RemedyStatusAllOpenCampaignsMapped
            if (colId === 'RemedyStatusAllOpenCampaigns') {
              const cellText = String(data.cell.raw ?? '').trim();
              if (!cellText) return;

              // Parse each line: "black '@' 11 - Status text"
              // Format: "{color} '@' {iconCode} - {label}"
              const lines = cellText.split('\n').filter(Boolean);

              const remedyRaw = rowObj?.[colId];
              const remedyList = Array.isArray(remedyRaw)
                ? remedyRaw
                : remedyRaw && typeof remedyRaw === 'object'
                  ? [remedyRaw]
                  : [];

              const getDefectStopSaleFlag = (remedyItem: any): any => {
                return (
                  remedyItem?.defectStopSaleFlag ??
                  remedyItem?.defectStopSaleFlg ??
                  remedyItem?.defect_stop_sale_flag ??
                  remedyItem?.DefectStopSaleFlag ??
                  remedyItem?.DefectStopSaleFlg ??
                  null
                );
              };

              const isTruthyStopSaleFlag = (flag: any): boolean => {
                const normalized = String(flag ?? '')
                  .trim()
                  .toLowerCase();
                return (
                  flag === true ||
                  flag === 1 ||
                  normalized === 'y' ||
                  normalized === 'yes' ||
                  normalized === 'true' ||
                  normalized === '1'
                );
              };

              const parsedRemedyEntries: Array<{
                iconCode: string;
                text: string;
                isRed: boolean;
              }> = [];

              // ← Strip color prefix and icon code — only show label text
              // "black '@' 11 - Status text" → "Status text"
              const hasTaggedPrefix = lines.some((line: string) =>
                /^(red|black)\s*'@'/i.test(line.trimStart())
              );
              if (hasTaggedPrefix) {
                lines.forEach((line: string) => {
                  const normalized = line.trimStart();
                  const colorMatch = normalized.match(/^(red|black)\s*'@'/i);
                  const iconMatch = normalized.match(/'@'\s*([^\-]+?)\s*-/i);
                  const text = normalized
                    .replace(/^(red|black)\s*'@'\s*/i, '')
                    .replace(/^[^\-]+?\s*-\s*/, '')
                    .trim();

                  parsedRemedyEntries.push({
                    iconCode: (iconMatch?.[1] ?? '').trim(),
                    text,
                    isRed:
                      String(colorMatch?.[1] ?? '').toLowerCase() === 'red',
                  });
                });
              } else if (remedyList.length > 0) {
                remedyList.forEach((item: any) => {
                  const iconCode = item?.remedyStatusCode
                    ? String(item.remedyStatusCode).trim()
                    : '';
                  const status = String(item?.remedyStatus ?? '').trim();
                  const text = status;
                  if (!text) {
                    return;
                  }
                  parsedRemedyEntries.push({
                    iconCode,
                    text,
                    isRed: isTruthyStopSaleFlag(getDefectStopSaleFlag(item)),
                  });
                });
              }

              // Use pre-computed column width from columnStyles (reliable at parse time).
              // getRemedyCellWidth(data) falls back to 160 during didParseCell because
              // AutoTable hasn't laid out cells yet — that wrong width causes under-wrapping
              // and an underestimated minCellHeight which clips text.
              const knownColWidth = columnStyles[data.column.index]?.cellWidth;
              const cellW =
                knownColWidth && knownColWidth > 0
                  ? knownColWidth
                  : getRemedyCellWidth(data);
              const iconGapLeft = 22;
              const paddingRight = 4;
              const textMaxWidth = Math.max(
                cellW - iconGapLeft - paddingRight,
                20
              );
              const cellFontSize = Number(data.cell.styles?.fontSize ?? 7);
              const lineHeight = Math.max(getPdfLineHeight(cellFontSize), 2.6);
              const topPadding = 5;
              const bottomPadding = 5;

              const drawLines: Array<{
                text: string;
                isRed: boolean;
                iconCode?: string;
              }> = [];

              for (const entry of parsedRemedyEntries) {
                const wrappedLines =
                  ((doc as any).splitTextToSize(
                    entry.text || '',
                    textMaxWidth
                  ) as string[]) || [];
                const normalizedWrapped = wrappedLines.length
                  ? wrappedLines
                  : [''];

                for (let idx = 0; idx < normalizedWrapped.length; idx += 1) {
                  drawLines.push({
                    text: normalizedWrapped[idx],
                    isRed: entry.isRed,
                    iconCode: idx === 0 ? entry.iconCode : undefined,
                  });
                }
              }

              remedyRenderByCell.set(
                `${data.row.index}-${data.column.index}`,
                drawLines
              );

              const totalWrappedLines = drawLines.length;

              const requiredMinHeight =
                topPadding +
                bottomPadding +
                Math.max(totalWrappedLines, 1) * lineHeight;
              data.cell.styles.minCellHeight = Math.max(
                data.cell.styles.minCellHeight ?? 0,
                requiredMinHeight
              );

              // Render remedy text manually in didDrawCell so each line can have its own color.
              data.cell.text = '';
              data.cell.styles.textColor = [14, 14, 14];

              // Left padding to make room for icon
              data.cell.styles.cellPadding = {
                left: iconGapLeft,
                top: topPadding,
                right: paddingRight,
                bottom: bottomPadding,
              };
              return;
            }

            // Existing red color logic for other columns based on Color field
            const ICDtargetCols = new Set([
              'DefectCode',
              'DefectDescription',
              'BulletinNumber',
              'RecallNumber',
              'CampaignStartDate',
            ]);
            const VCDtargetCols = new Set(['RemedyStatusAllOpenCampaigns']);

            const isRed =
              String(rowObj?.ICDColor ?? '')
                .trim()
                .toLowerCase() === 'red';
            if (isRed && ICDtargetCols.has(colId)) {
              data.cell.styles.textColor = [220, 20, 60]; // match .color-red (#c62828)
            }
            const isVCDRed =
              String(rowObj?.VCDColor ?? '')
                .trim()
                .toLowerCase() === 'red';
            if (isVCDRed && VCDtargetCols.has(colId)) {
              data.cell.styles.textColor = [220, 20, 60]; // match .color-red (#c62828)
            }
          } catch {}
        },
        didDrawCell: (data: any) => {
          if (!options?.renderIcons || data.section !== 'body') return;
          try {
            const colId = columns[data.column.index];
            const raw = data.cell.raw;
            const x = data.cell.x;
            const y = data.cell.y;
            const h = data.cell.height;
            const defaultText = options?.textColor ?? [26, 26, 26];
            // StopSale icon rendering
            if (colId === 'StopSale') {
              const truthy =
                raw === true ||
                raw === 1 ||
                String(raw).toLowerCase() === 'y' ||
                String(raw).toLowerCase() === 'yes' ||
                String(raw).toLowerCase() === 'true';
              if (truthy) {
                const cellW = data.cell.width ?? data.cell._width ?? 24;
                if (stopSaleImg) {
                  const iconH = Math.min(h - 6, 16);
                  const iconW = iconH; // square icon
                  const ix = x + (cellW - iconW) / 2;
                  const iy = y + (h - iconH) / 2;
                  // Use base64 data URL directly
                  (doc as any).addImage(
                    stopSaleImg,
                    'PNG',
                    ix,
                    iy,
                    iconW,
                    iconH
                  );
                }
              }
            }
            if (colId === 'OverallRemedyAvailable') {
              const oraIcon = String(raw).toLowerCase();

              if (oraIcon) {
                const cellW = data.cell.width ?? data.cell._width ?? 24;
                if (oraIcon) {
                  const iconH = Math.min(h - 6, 16);
                  const iconW = iconH; // square icon
                  const ix = x + (cellW - iconW) / 2;
                  const iy = y + (h - iconH) / 2;
                  // Use base64 data URL directly
                  switch (oraIcon) {
                    case 'y':
                      (doc as any).addImage(
                        oraIconY,
                        'PNG',
                        ix,
                        iy,
                        iconW,
                        iconH
                      );
                      break;
                    case 'n':
                      (doc as any).addImage(
                        oraIconN,
                        'PNG',
                        ix,
                        iy,
                        iconW,
                        iconH
                      );
                      break;
                    case 's':
                      (doc as any).addImage(
                        oraIconS,
                        'PNG',
                        ix,
                        iy,
                        iconW,
                        iconH
                      );
                      break;
                    default:
                      break;
                  }
                }
              }
            }
            if (colId === 'RemedyStatusAllOpenCampaigns') {
              const cellKey = `${data.row.index}-${data.column.index}`;
              const entries = remedyRenderByCell.get(cellKey) ?? [];
              if (!entries.length) {
                return;
              }

              // Use the same reliable width source as didParseCell for consistent wrapping.
              const knownColWidthDraw =
                columnStyles[data.column.index]?.cellWidth;
              const cellW =
                knownColWidthDraw && knownColWidthDraw > 0
                  ? knownColWidthDraw
                  : getRemedyCellWidth(data);
              const iconGapLeft = 22;
              const paddingRight = 4;
              const textMaxWidth = Math.max(
                cellW - iconGapLeft - paddingRight,
                20
              );
              const cellFontSize = Number(data.cell.styles?.fontSize ?? 7);
              const lineHeight = Math.max(getPdfLineHeight(cellFontSize), 2.6);
              const topPadding = 5;
              const textX = x + iconGapLeft;
              const iconH = Math.max(Math.min(lineHeight - 1, 10), 5);
              const iconW = iconH;

              const drawLines: Array<{
                text: string;
                isRed: boolean;
                iconCode?: string;
              }> = [];

              for (const entry of entries) {
                const wrappedLines =
                  ((doc as any).splitTextToSize(
                    entry.text || '',
                    textMaxWidth
                  ) as string[]) || [];
                const normalizedWrapped = wrappedLines.length
                  ? wrappedLines
                  : [''];

                for (let idx = 0; idx < normalizedWrapped.length; idx += 1) {
                  drawLines.push({
                    text: normalizedWrapped[idx],
                    isRed: entry.isRed,
                    iconCode: idx === 0 ? entry.iconCode : undefined,
                  });
                }
              }

              drawLines.forEach((line, i) => {
                const lineTop = y + topPadding + i * lineHeight;
                const iy = lineTop + (lineHeight - iconH) / 2;
                const ix = x + 4;

                if (line.iconCode === '11' && remedyStatusIconY) {
                  (doc as any).addImage(
                    remedyStatusIconY,
                    'PNG',
                    ix,
                    iy,
                    iconW,
                    iconH
                  );
                } else if (line.iconCode === '12' && remedyStatusIconN) {
                  (doc as any).addImage(
                    remedyStatusIconN,
                    'PNG',
                    ix,
                    iy,
                    iconW,
                    iconH
                  );
                }

                (doc as any).setTextColor(
                  line.isRed ? 220 : 14,
                  line.isRed ? 0 : 14,
                  line.isRed ? 0 : 14
                );
                const textY = lineTop + lineHeight * 0.8;
                (doc as any).text(line.text, textX, textY);
              });

              (doc as any).setTextColor(
                defaultText[0],
                defaultText[1],
                defaultText[2]
              );
              remedyRenderByCell.delete(cellKey);
              return;
            }
          } catch {}
        },
        theme: 'grid',
      });

      // Replace total pages placeholders now that all content is rendered
      try {
        (doc as any).putTotalPages(totalPagesExp);
      } catch {}

      // Draw END OF REPORT directly below the table on the last page
      try {
        const finalPages = (doc as any).getNumberOfPages();
        (doc as any).setPage(finalPages);
        const finalY = (doc as any).lastAutoTable?.finalY;
        if (typeof finalY === 'number') {
          // Label centered below the rule
          doc.setFontSize(12);
          try {
            (doc as any).setFont(undefined, 'bold');
          } catch {}
          doc.text('END OF REPORT', pageWidth / 2, finalY + 20, {
            align: 'center',
          });
          try {
            (doc as any).setFont(undefined, 'normal');
          } catch {}
          // Thin rule below the END OF REPORT label
          doc.setDrawColor(180);
          doc.setLineWidth(0.5);
          doc.line(marginLeft, finalY + 6, pageWidth - marginRight, finalY + 6);
        }
      } catch {}

      //  Trigger download via the robust triggerDownload() helper
      // console.log('Generating PDF blob and triggering download');
      const pdfBlob = doc.output('blob');
      // console.log('PDF blob size:', pdfBlob.size, 'bytes');
      this.triggerDownload(
        pdfBlob,
        `${fileName || 'export'}.pdf`,
        'application/pdf',
        onComplete
      );
    } catch (e) {
      // Log the actual error so you can debug in INT
      console.error('PDF export failed — falling back to print:', e);
      if (e instanceof Error) {
        console.error('Error details:');
        console.error('  Message:', e.message);
        console.error('  Name:', e.name);
        console.error('  Stack:', e.stack);
      } else {
        console.error('Error details (non-Error):', String(e));
      }
      this.printWithDetails(rows, columns, details, { title: options?.title });
      onComplete?.(
        false,
        'PDF generation failed; print dialog opened as fallback.'
      );
    }
  }
}
